import { useState, useEffect } from 'react';
import { useLang } from '../hooks/useLang';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ChevronDown, ChevronUp, Phone, Mail, MapPin, ExternalLink } from 'lucide-react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import SectionReveal from '../components/SectionReveal';
import { t } from '../translations';

const HERO_BG    = 'https://images.pexels.com/photos/29923357/pexels-photo-29923357.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920';
const JSB_LOGO   = '/jsb-logo.png';
const UTV_LOGO   = 'https://koberenergy.com/assets/utv-logo.png';
const TESLA_LOGO = 'https://teslagroup.eu/wp-content/uploads/2023/05/Tesla-group-logo-white.svg';

// Animation constants — extracted to avoid new object references on every render
const CI_ANIM_BACK  = { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5 } };
const CI_ANIM_LABEL = { initial: { opacity: 0, y: 20 },  animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.1 } };
const CI_ANIM_TITLE = { initial: { opacity: 0, y: 30 },  animate: { opacity: 1, y: 0 }, transition: { duration: 0.8, delay: 0.25 } };
const CI_ANIM_SUB   = { initial: { opacity: 0, y: 24 },  animate: { opacity: 1, y: 0 }, transition: { duration: 0.7, delay: 0.45 } };
const CI_ANIM_BTNS  = { initial: { opacity: 0, y: 16 },  animate: { opacity: 1, y: 0 }, transition: { duration: 0.6, delay: 0.65 } };

const PAGE_TEXT = {
  EN: {
    hero: {
      label: "KÖBER Energy Holding — Commercial & Industrial",
      title: "C&I Energy Solutions",
      subtitle: "Köber Energy finances, installs, and operates the photovoltaic system at the client premises and delivers to the client a long-term PPA at a fixed price.",
      cta: "Request Free Assessment",
    },
    challenge: {
      label: "The Challenge",
      title: "Energy Costs Are Eroding Your Margins",
      intro: "Energy prices have become unpredictable — placing direct pressure on your business's bottom line.",
      items: [
        { title: "Rising Bills", body: "Energy prices apply constant pressure on your operational costs, month after month." },
        { title: "Price Volatility", body: "Market fluctuations make annual budgeting nearly impossible." },
        { title: "Grid Dependency", body: "You are fully exposed to price shocks and supply instability." },
      ],
      callout: "The solution: your own solar energy, no hidden costs — zero investment, zero risk.",
    },
    model: {
      label: "The Solution",
      title: "PPA: Solar Energy, Zero Investment",
      intro: "Through a Power Purchase Agreement (PPA), we build the system — you only buy the energy it produces.",
      items: [
        { title: "Zero Upfront Investment", body: "Köber Energy fully finances and installs the photovoltaic system at your premises.", featured: true },
        { title: "No Hidden Costs", body: "You pay only for the energy produced, at a price lower than the grid — no surprises." },
        { title: "Full Management", body: "We handle design, installation, operation, and maintenance — completely." },
        { title: "Savings from Day One", body: "Start reducing costs immediately, with no capital tied up in equipment." },
      ],
    },
    howItWorks: {
      label: "The PPA Model",
      title: "How It Works",
      subtitle: "Three steps — from installation to savings — no effort and no cost from your side.",
      steps: [
        { num: "01", title: "We Invest & Install", body: "Köber Energy finances, designs, and installs the solar system at your premises — fully turnkey." },
        { num: "02", title: "You Consume Green Energy", body: "Use locally produced energy at a price lower than the grid — no hidden costs, no surprises." },
        { num: "03", title: "You Save & Stay Protected", body: "Pay less than the grid and stay protected from sudden energy price increases." },
      ],
      note: "You pay exclusively for the energy produced by the panels — no hidden costs, no surprises.",
    },
    impact: {
      label: "Financial Impact",
      title: "Immediate & Measurable Savings",
      stats: [
        { value: "−33%", label: "annual energy bill reduction" },
        { value: "< Grid", label: "price — lower than what you pay now" },
        { value: "100%", label: "self-consumption potential" },
        { value: "€1.7M", label: "cumulative savings over 10-year contract" },
      ],
    },
    riskFree: {
      label: "Zero Risk",
      title: "All Risks Remain with Us",
      subtitle: "You focus on your business. We manage the solar system — completely.",
      items: [
        { title: "Full 24/7 Management", body: "Monitoring, operation, and maintenance throughout the entire contract duration." },
        { title: "Premium Tier-1 Equipment", body: "Jinko solar panels and Huawei inverters, with guaranteed performance." },
        { title: "Extended Warranties — up to 15 Years", body: "For all installed equipment — delivering long-term reliability." },
        { title: "Full Insurance Coverage", body: "Covers the solar system, your building, and all personnel involved in operations." },
      ],
    },
    ownership: {
      label: "After the Contract",
      title: "At Contract End, the Installation Is Yours",
      body: "At the end of the PPA contract, the photovoltaic system is transferred to you free of charge. All equipment is verified and reconditioned — so you enjoy years of worry-free, near-free energy.",
      benefits: [
        "Ownership transfer at zero cost",
        "Equipment verified and reconditioned at transfer",
        "Near-free energy for years to come",
        "Real, lasting energy independence",
      ],
      timeline: [
        { phase: "Year 0 — Installation", desc: "We finance and install the system. Your investment: €0." },
        { phase: "Years 1–10 — No Hidden Costs", desc: "You pay only for the energy produced, at a price lower than the grid." },
        { phase: "After Contract — Ownership", desc: "The installation is yours. Near-free energy for years ahead." },
      ],
    },
    whyKober: {
      label: "Your Partner",
      title: "Why Köber Energy",
      stats: [
        { value: "1.1 GWp", label: "contracted capacity" },
        { value: "429 MWp", label: "installed to date" },
        { value: "3,500+", label: "satisfied clients" },
      ],
      items: [
        { title: "PPA Solutions — No Capital Blocked", body: "We reduce energy costs and completely eliminate financial risk for your business." },
        { title: "Dedicated C&I Expertise", body: "Solar energy solutions for commercial & industrial companies in Romania and Europe." },
        { title: "Scalable Systems", body: "From 100 kWp to over 6 MWp — rooftop, ground-mounted, or with storage." },
        { title: "Sustainability Partner", body: "We turn energy into a clean and durable competitive advantage." },
      ],
    },
    portfolio: {
      label: "Portfolio",
      title: "Projects Executed with Our Partners",
      subtitle: "Over 38 photovoltaic projects across industry, real estate, infrastructure, and utilities.",
      stats: [
        { value: "38", label: "reference projects" },
        { value: "4", label: "sectors" },
        { value: "8", label: "countries" },
      ],
      download: "",
    },
    consortium: {
      label: "Our Consortium",
      title: "A Consortium of Energy & Technology Leaders",
      body: "The PPA solution is delivered by a consortium of Köber Energy, United Technologies Ventures, and Tesla Group — combining expertise in renewable energy development, financing structuring, and storage technology at European scale.",
    },
    faq: {
      label: "FAQ",
      title: "Quick Answers About PPA",
      items: [
        { q: "What is a PPA?", a: "A contract by which Köber Energy installs and operates the solar system without any investment from you — you pay only for the energy consumed, at a fixed price." },
        { q: "What are the main advantages?", a: "Immediate savings on your energy bill, self-consumption of up to 100%, and all technical and financial risks assumed by Köber Energy." },
        { q: "How long does installation take?", a: "Free evaluation, design, and turnkey installation — typically 1–3 months, with all permits included." },
        { q: "What happens at the end of the contract?", a: "The solar installation is transferred to you free of charge and becomes fully your property. Equipment is verified and reconditioned for years of worry-free operation." },
        { q: "What documents are needed for a quote?", a: "Tax ID, last 12 months of energy bills (or consumption curves), contracted power / ATR copy, location and desired placement, working schedule, and a contact person." },
      ],
    },
    cta: {
      label: "Get Started",
      title: "Let's Reduce Your Energy Bill.",
      subtitle: "Schedule a free site assessment and receive a personalised offer with an exact savings projection — with no commitment.",
      phone: "0767 171 420",
      email: "info@koberenergy.com",
      address: "18 Cluceru Udricani Street, 030782, 3rd District, Bucharest, Romania",
      btnPrimary: "Request Free Assessment",
      btnVisit: "Visit Köber Energy",
    },
  },
  RO: {
    hero: {
      label: "KÖBER Energy Holding — Comercial și Industrial",
      title: "Soluții Energetice C&I",
      subtitle: "Köber Energy finanțează, instalează și operează sistemul fotovoltaic la sediul clientului și livrează clientului un PPA pe termen lung la preț fix.",
      cta: "Solicită Evaluare Gratuită",
    },
    challenge: {
      label: "Provocarea",
      title: "Energia Scumpă Îți Erodează Profitul",
      intro: "Costurile cu energia au devenit imprevizibile — și apasă direct pe marja afacerii tale.",
      items: [
        { title: "Facturi Tot Mai Mari", body: "Prețul energiei pune o presiune constantă pe costurile tale operaționale, lună de lună." },
        { title: "Prețuri Volatile", body: "Fluctuațiile pieței fac bugetarea aproape imposibilă de la un an la altul." },
        { title: "Dependență de Rețea", body: "Ești expus integral la șocurile de preț și la instabilitatea furnizării." },
      ],
      callout: "Soluția: energie solară proprie, fără costuri ascunse — fără investiție și fără risc.",
    },
    model: {
      label: "Soluția",
      title: "PPA: Energie Solară, Zero Investiție",
      intro: "Printr-un contract PPA (Power Purchase Agreement), noi construim sistemul — tu cumperi doar energia produsă.",
      items: [
        { title: "Zero Investiție Inițială", body: "Köber Energy finanțează și instalează complet sistemul fotovoltaic la sediul tău.", featured: true },
        { title: "Niciun Cost Ascuns", body: "Plătești doar energia produsă, la un preț mai mic decât din rețea — fără surprize." },
        { title: "Management Complet", body: "Ne ocupăm de proiectare, instalare, operare și mentenanță — integral." },
        { title: "Economii din Prima Zi", body: "Începi să reduci costurile imediat, fără capital blocat în echipamente." },
      ],
    },
    howItWorks: {
      label: "Modelul PPA",
      title: "Cum Funcționează",
      subtitle: "Trei pași — de la instalare la economii — fără efort și fără cost din partea ta.",
      steps: [
        { num: "01", title: "Noi Investim și Instalăm", body: "Köber Energy finanțează, proiectează și instalează sistemul solar la sediul tău, complet la cheie." },
        { num: "02", title: "Tu Consumi Energie Verde", body: "Folosești energia produsă local, la un preț mai mic decât din rețea — fără costuri ascunse." },
        { num: "03", title: "Economisești și Ești Protejat", body: "Plătești mai puțin decât din rețea și ești ferit de creșterile bruște de preț." },
      ],
      note: "Plătești exclusiv pentru energia produsă de panouri — fără costuri ascunse, fără surprize.",
    },
    impact: {
      label: "Impact Financiar",
      title: "Economii Imediate și Măsurabile",
      stats: [
        { value: "−33%", label: "reducere a facturii anuale de energie" },
        { value: "< Rețea", label: "preț mai mic decât plătești acum" },
        { value: "100%", label: "autoconsum posibil" },
        { value: "1,7 mil. €", label: "economii totale după 10 ani de contract" },
      ],
    },
    riskFree: {
      label: "Fără Griji",
      title: "Toate Riscurile Rămân la Noi",
      subtitle: "Tu te ocupi de afacerea ta. De sistemul solar ne ocupăm noi — integral.",
      items: [
        { title: "Management Complet 24/7", body: "Monitorizare, operare și mentenanță pe toată durata contractului." },
        { title: "Echipamente Premium Tier-1", body: "Panouri solare Jinko și invertoare Huawei, cu performanță garantată." },
        { title: "Garanții Extinse — până la 15 Ani", body: "Pentru echipamentele instalate — fiabilitate pe termen lung." },
        { title: "Asigurare Completă", body: "Acoperă sistemul solar, clădirea ta și personalul implicat în operare." },
      ],
    },
    ownership: {
      label: "La Finalul Contractului",
      title: "La Expirarea Contractului, Instalația Este a Ta",
      body: "La încheierea contractului PPA, sistemul fotovoltaic îți este transferat gratuit. Toate echipamentele sunt verificate și recondiționate — pentru ani de funcționare fără griji.",
      benefits: [
        "Transfer de proprietate fără niciun cost",
        "Echipamente verificate și recondiționate la transfer",
        "Energie aproape gratuită, ani la rând",
        "Independență energetică reală și durabilă",
      ],
      timeline: [
        { phase: "Anul 0 — Instalare", desc: "Finanțăm și instalăm sistemul. Investiția ta: 0 €." },
        { phase: "Anii 1–10 — Niciun Cost Ascuns", desc: "Plătești doar energia produsă, la un preț mai mic decât din rețea." },
        { phase: "După Contract — Proprietate", desc: "Instalația devine a ta. Energie aproape gratuită, ani la rând." },
      ],
    },
    whyKober: {
      label: "Partenerul Tău",
      title: "De Ce Köber Energy",
      stats: [
        { value: "1,1 GWp", label: "capacitate contractată" },
        { value: "429 MWp", label: "instalați până în prezent" },
        { value: "3.500+", label: "clienți mulțumiți" },
      ],
      items: [
        { title: "Soluții PPA Fără Capital Blocat", body: "Reducem costurile energetice și eliminăm complet riscul financiar al afacerii tale." },
        { title: "Expertiză Dedicată Sectorului C&I", body: "Energie solară pentru companii comerciale și industriale din România și Europa." },
        { title: "Sisteme Scalabile", body: "De la 100 kWp la peste 6 MWp — rooftop, montare pe sol sau cu stocare." },
        { title: "Partener pentru Sustenabilitate", body: "Transformăm energia într-un avantaj competitiv, curat și durabil." },
      ],
    },
    portfolio: {
      label: "Portofoliu",
      title: "Proiecte Executate Alături de Partenerii Noștri",
      subtitle: "Peste 38 de proiecte fotovoltaice în industrie, imobiliare, infrastructură și utilități.",
      stats: [
        { value: "38", label: "proiecte de referință" },
        { value: "4", label: "sectoare de activitate" },
        { value: "8", label: "țări" },
      ],
      download: "Descarcă Portofoliul Complet (PDF)",
    },
    consortium: {
      label: "Consorțiul Nostru",
      title: "Un Consorțiu de Lideri în Energie și Tehnologie",
      body: "Soluția PPA este livrată de un consorțiu format din Köber Energy, United Technologies Ventures și Tesla Group — combinând expertiza în dezvoltarea proiectelor de energie regenerabilă, structurarea finanțării și tehnologia de stocare la nivel european.",
    },
    faq: {
      label: "Întrebări Frecvente",
      title: "Răspunsuri Rapide despre PPA",
      items: [
        { q: "Ce este un PPA?", a: "Un contract prin care Köber Energy instalează și operează sistemul solar fără investiții din partea ta — plătești doar energia consumată, la preț fix." },
        { q: "Care sunt avantajele principale?", a: "Economii imediate la factură, autoconsum de până la 100% și toate riscurile tehnice și financiare asumate de Köber Energy." },
        { q: "Cât durează instalarea?", a: "Evaluare gratuită, proiectare și instalare la cheie — de regulă în 1–3 luni, cu autorizațiile incluse." },
        { q: "Ce se întâmplă la finalul contractului?", a: "Instalația solară îți este transferată gratuit și devine complet proprietatea ta. Echipamentele sunt verificate și recondiționate pentru ani de funcționare fără griji." },
        { q: "Ce documente îmi cereți pentru o ofertă?", a: "CUI, ultimele 12 luni de facturi de energie (sau curbele de consum), puterea contractată / copie ATR, locația și amplasamentul dorit, programul de lucru și o persoană de contact." },
      ],
    },
    cta: {
      label: "Hai să Începem",
      title: "Hai să-ți Reducem Factura la Energie.",
      subtitle: "Programează o evaluare gratuită a locației tale și primește o ofertă personalizată, cu proiecția exactă a economiilor — fără niciun angajament.",
      phone: "0767 171 420",
      email: "info@koberenergy.com",
      address: "Str. Cluceru Udricani nr. 18, 030782, Sectorul 3, București, România",
      btnPrimary: "Solicită Evaluare Gratuită",
      btnVisit: "Vizitează Köber Energy",
    },
  },
};

// ── Sub-components ─────────────────────────────────────────────────────────

function CIHero({ p, onContact, onBack }) {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[#050505]">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('${HERO_BG}')`, opacity: 0.28 }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black/98 via-black/80 to-black/50" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/30" />
      <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-[#D4A017] via-[#D4A017]/40 to-transparent" />

      <div className="relative z-10 container mx-auto px-6 lg:px-12 max-w-7xl pt-44 pb-20">
        <motion.button
          {...CI_ANIM_BACK}
          onClick={onBack}
          className="flex items-center gap-2 text-sm text-white/40 hover:text-[#D4A017] transition-colors duration-200 mb-10"
        >
          <ArrowLeft size={14} /> Back to Home
        </motion.button>

        <div className="max-w-3xl">
          <motion.p
            {...CI_ANIM_LABEL}
            className="text-xs tracking-[0.3em] uppercase text-[#D4A017] mb-5 font-medium"
          >
            {p.label}
          </motion.p>
          <motion.h1
            {...CI_ANIM_TITLE}
            className="text-5xl sm:text-6xl lg:text-7xl font-semibold text-white leading-tight tracking-tight mb-6"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            {p.title}
          </motion.h1>
          <motion.p
            {...CI_ANIM_SUB}
            className="text-lg text-white/60 leading-relaxed mb-10 max-w-2xl"
          >
            {p.subtitle}
          </motion.p>
          <motion.div
            {...CI_ANIM_BTNS}
            className="flex flex-col sm:flex-row gap-4"
          >
            <button
              data-testid="ci-hero-cta-btn"
              onClick={onContact}
              className="group inline-flex items-center justify-center gap-3 bg-[#D4A017] hover:bg-[#F5B814] text-black font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-200"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              {p.cta} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <a
              href="https://koberenergy.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white/80 hover:border-[#D4A017]/60 hover:text-[#D4A017] px-8 py-4 text-sm tracking-wide transition-colors duration-200"
            >
              koberenergy.com <ExternalLink size={13} />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CIChallenge({ p }) {
  return (
    <section className="py-24 sm:py-32 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4 max-w-2xl" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
          <p className="text-base text-white/55 mb-14 max-w-xl">{p.intro}</p>
        </SectionReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {p.items.map((item, i) => (
            <SectionReveal key={item.title} delay={i * 100}>
              <div className="border border-white/10 bg-[#0d0d0d] p-7 hover:border-[#D4A017]/30 transition-colors duration-300 h-full">
                <div className="w-8 h-0.5 bg-[#D4A017] mb-5" />
                <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{item.body}</p>
              </div>
            </SectionReveal>
          ))}
        </div>
        <SectionReveal delay={200}>
          <div className="border-l-2 border-[#D4A017] pl-6 py-2">
            <p className="text-base font-medium text-white/80 italic">{p.callout}</p>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
}

/** Returns the badge label for a featured model card, or empty string if none. */
function getFeaturedLabel(item, index) {
  const isZeroInvestment = item.title.includes("Zero") || item.title.includes("Niciun");
  if (isZeroInvestment && index === 0) return "Recommended";
  return "";
}

function CIModel({ p }) {
  return (
    <section className="py-24 sm:py-32 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
          <p className="text-base text-white/55 mb-14 max-w-xl">{p.intro}</p>
        </SectionReveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {p.items.map((item, i) => (
            <SectionReveal key={item.title} delay={i * 90}>
              <div className={`p-7 h-full ${item.featured ? 'bg-[#D4A017]/8 border border-[#D4A017]/35' : 'bg-[#0d0d0d] border border-white/10 hover:border-[#D4A017]/25 transition-colors duration-300'}`}>
                {item.featured && (
                  <span className="inline-block text-xs font-semibold tracking-widest uppercase text-[#D4A017] mb-3 border border-[#D4A017]/40 px-2.5 py-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {getFeaturedLabel(item, i)}
                  </span>
                )}
                <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{item.body}</p>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CIHowItWorks({ p }) {
  return (
    <section className="py-24 sm:py-32 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium text-center">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl font-medium text-white mb-3 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
          <p className="text-base text-white/50 text-center mb-16 max-w-xl mx-auto">{p.subtitle}</p>
        </SectionReveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0">
          {p.steps.map((step, i) => (
            <SectionReveal key={step.num} delay={i * 120}>
              <div className="relative p-8 border border-white/8 bg-[#0d0d0d] md:border-r-0 last:border-r md:last:border-r border-b md:border-b-0">
                <div
                  className="text-5xl font-semibold mb-5 tabular-nums"
                  style={{ fontFamily: "'Outfit', sans-serif", color: 'rgba(212,160,23,0.15)' }}
                >
                  {step.num}
                </div>
                <h3 className="text-base font-semibold text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{step.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{step.body}</p>
                {i < p.steps.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 z-10 w-8 h-8 bg-[#0d0d0d] border border-white/8 rotate-45" />
                )}
              </div>
            </SectionReveal>
          ))}
        </div>
        <SectionReveal delay={300}>
          <p className="mt-8 text-sm text-white/35 text-center italic">{p.note}</p>
        </SectionReveal>
      </div>
    </section>
  );
}

function CIImpact({ p }) {
  return (
    <section className="py-24 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium text-center">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl font-medium text-white mb-16 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
        </SectionReveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {p.stats.map((stat, i) => (
            <SectionReveal key={stat.label} delay={i * 80}>
              <div className="border border-white/10 bg-[#0d0d0d] p-6 hover:border-[#D4A017]/35 transition-colors duration-300 text-center">
                <div
                  className="text-3xl sm:text-4xl font-semibold text-[#D4A017] mb-2 tabular-nums"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  {stat.value}
                </div>
                <div className="text-xs text-white/40 leading-relaxed">{stat.label}</div>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CIRiskFree({ p }) {
  return (
    <section className="py-24 sm:py-32 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
          <p className="text-base text-white/55 mb-14 max-w-xl">{p.subtitle}</p>
        </SectionReveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {p.items.map((item, i) => (
            <SectionReveal key={item.title} delay={i * 90}>
              <div className="flex gap-4 p-6 border border-white/10 bg-[#0d0d0d] hover:border-[#D4A017]/25 transition-colors duration-300">
                <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full mt-0.5" style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.35)' }}>
                  <Check size={13} color="#D4A017" strokeWidth={2.5} />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-white/45 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Returns the timeline dot background color based on step index. */
function getTimelineDotColor(index) {
  if (index === 0) return '#D4A017';
  if (index === 1) return 'rgba(212,160,23,0.5)';
  return 'rgba(212,160,23,0.25)';
}

function CIOwnership({ p }) {
  return (
    <section className="py-24 sm:py-32 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <SectionReveal>
            <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{p.label}</p>
            <h2 className="text-3xl sm:text-4xl font-medium text-white mb-6 leading-snug" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
            <p className="text-base text-white/55 leading-relaxed mb-8">{p.body}</p>
            <div className="space-y-3">
              {p.benefits.map((b) => (
                <div key={b} className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center rounded-full" style={{ background: 'rgba(212,160,23,0.12)', border: '1px solid rgba(212,160,23,0.35)' }}>
                    <Check size={10} color="#D4A017" strokeWidth={2.5} />
                  </div>
                  <span className="text-sm text-white/65">{b}</span>
                </div>
              ))}
            </div>
          </SectionReveal>
          <SectionReveal delay={150}>
            <div className="space-y-0">
              {p.timeline.map((item, i) => (
                <div key={item.phase} className="flex gap-5">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full flex-shrink-0 mt-1" style={{ background: getTimelineDotColor(i) }} />
                    {i < p.timeline.length - 1 && <div className="w-px flex-1 my-1" style={{ background: 'rgba(212,160,23,0.2)' }} />}
                  </div>
                  <div className="pb-8">
                    <p className="text-xs uppercase tracking-widest text-[#D4A017]/70 mb-1 font-medium">{item.phase}</p>
                    <p className="text-sm text-white/55 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
}

function CIWhyKober({ p }) {
  return (
    <section className="py-24 sm:py-32 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl font-medium text-white mb-14" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
        </SectionReveal>
        <div className="grid grid-cols-3 gap-8 mb-16 border-b border-white/8 pb-14">
          {p.stats.map((s, i) => (
            <SectionReveal key={s.label} delay={i * 80}>
              <div className="text-center">
                <div className="text-3xl sm:text-4xl font-semibold text-[#D4A017] mb-1 tabular-nums" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.value}</div>
                <div className="text-xs text-white/40">{s.label}</div>
              </div>
            </SectionReveal>
          ))}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {p.items.map((item, i) => (
            <SectionReveal key={item.title} delay={i * 80}>
              <div className="flex gap-4 p-6 border border-white/10 bg-[#0d0d0d]">
                <div className="w-1 flex-shrink-0 rounded-full" style={{ background: '#D4A017' }} />
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1.5" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-white/45 leading-relaxed">{item.body}</p>
                </div>
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CIPortfolio({ p }) {
  return (
    <section className="py-24 sm:py-32 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl font-medium text-white mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
          <p className="text-base text-white/50 mb-12 max-w-2xl">{p.subtitle}</p>
        </SectionReveal>

        <div className="grid grid-cols-3 gap-4 mb-10">
          {p.stats.map((s, i) => (
            <SectionReveal key={s.label} delay={i * 80}>
              <div className="border border-white/10 bg-[#0d0d0d] p-5 text-center">
                <div className="text-3xl font-semibold text-[#D4A017] mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>{s.value}</div>
                <div className="text-xs text-white/35">{s.label}</div>
              </div>
            </SectionReveal>
          ))}
        </div>

      </div>
    </section>
  );
}

function CIConsortium({ p }) {
  return (
    <section className="py-20 bg-[#080808]">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium text-center">{p.label}</p>
          <h2 className="text-2xl sm:text-3xl font-medium text-white mb-4 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
          <p className="text-sm text-white/50 text-center max-w-2xl mx-auto mb-14">{p.body}</p>
        </SectionReveal>
        <SectionReveal delay={100}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-10 sm:gap-16">
            <a href="http://jsb-zj.com/" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity duration-300">
              <img src={JSB_LOGO} alt="JSB" className="h-8 w-auto" />
            </a>
            <div className="w-px h-10 bg-white/15 hidden sm:block" />
            <a href="https://utvromania.com/" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity duration-300">
              <img src={UTV_LOGO} alt="United Technologies Ventures" className="h-10 w-auto" />
            </a>
            <div className="w-px h-10 bg-white/15 hidden sm:block" />
            <a href="https://teslagroup.eu/" target="_blank" rel="noopener noreferrer" className="opacity-60 hover:opacity-100 transition-opacity duration-300">
              <img src={TESLA_LOGO} alt="Tesla Group" className="h-8 w-auto" />
            </a>
          </div>
        </SectionReveal>
      </div>
    </section>
  );
}

function CIFAQ({ p }) {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-24 sm:py-32 bg-[#050505]">
      <div className="container mx-auto px-6 lg:px-12 max-w-3xl">
        <SectionReveal>
          <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium text-center">{p.label}</p>
          <h2 className="text-3xl sm:text-4xl font-medium text-white mb-14 text-center" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
        </SectionReveal>
        <div className="space-y-0">
          {p.items.map((item, i) => (
            <SectionReveal key={item.q} delay={i * 60}>
              <div className="border-b border-white/8">
                <button
                  data-testid={`ci-faq-${i}`}
                  onClick={() => setOpen(open === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="text-sm font-medium text-white/85 group-hover:text-white">{item.q}</span>
                  {open === i
                    ? <ChevronUp size={15} className="text-[#D4A017] flex-shrink-0" />
                    : <ChevronDown size={15} className="text-white/30 flex-shrink-0" />}
                </button>
                {open === i && (
                  <p className="pb-5 text-sm text-white/50 leading-relaxed">{item.a}</p>
                )}
              </div>
            </SectionReveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function CICTA({ p, onContact }) {
  return (
    <section className="py-24 bg-[#080808] border-t border-white/8">
      <div className="container mx-auto px-6 lg:px-12 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
          <SectionReveal>
            <p className="text-xs tracking-[0.28em] uppercase text-[#D4A017] mb-4 font-medium">{p.label}</p>
            <h2 className="text-3xl sm:text-4xl font-medium text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{p.title}</h2>
            <p className="text-base text-white/55 mb-10">{p.subtitle}</p>
            <div className="space-y-4">
              <a href={`tel:${p.phone.replace(/\s/g,'')}`} className="flex items-center gap-3 text-sm text-white/50 hover:text-[#D4A017] transition-colors duration-200">
                <Phone size={14} className="text-[#D4A017]" /> {p.phone}
              </a>
              <a href={`mailto:${p.email}`} className="flex items-center gap-3 text-sm text-white/50 hover:text-[#D4A017] transition-colors duration-200">
                <Mail size={14} className="text-[#D4A017]" /> {p.email}
              </a>
              <div className="flex items-start gap-3 text-sm text-white/50">
                <MapPin size={14} className="text-[#D4A017] mt-0.5 flex-shrink-0" /> {p.address}
              </div>
            </div>
          </SectionReveal>
          <SectionReveal delay={150}>
            <div className="space-y-4">
              <button
                data-testid="ci-cta-contact-btn"
                onClick={onContact}
                className="group w-full inline-flex items-center justify-center gap-3 bg-[#D4A017] hover:bg-[#F5B814] text-black font-semibold px-8 py-4 text-sm tracking-wide transition-colors duration-200"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {p.btnPrimary} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
              <a
                href="https://koberenergy.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ci-visit-site-btn"
                className="w-full inline-flex items-center justify-center gap-2 border border-[#D4A017]/40 text-[#D4A017] hover:border-[#D4A017]/80 px-8 py-4 text-sm tracking-wide transition-colors duration-200"
              >
                {p.btnVisit} <ExternalLink size={14} />
              </a>
            </div>
          </SectionReveal>
        </div>
      </div>
    </section>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

const _OG_IMG = 'https://koberholding.com/og-transformers.jpg';

const SEO_CI = {
  EN: {
    title:    'C&I Energy Solutions — Solar PPA & On-Site Generation | KÖBER Energy Holding',
    description: 'KÖBER Energy finances, installs and operates photovoltaic systems at your premises, delivering a long-term PPA at a fixed price. Zero capex, predictable energy costs.',
    ogTitle:  'C&I Energy Solutions | KÖBER Energy Holding',
    ogDesc:   'Zero-capex solar PPA for commercial & industrial clients. KÖBER finances, installs and operates — you pay a fixed price per kWh.',
    locale:   'en_US',
  },
  RO: {
    title:    'Soluții Energetice C&I — PPA Solar | KÖBER Energy Holding',
    description: 'KÖBER Energy finanțează, instalează și operează sisteme fotovoltaice la sediul clientului, livrând un PPA pe termen lung la preț fix. Zero capex, costuri energetice predictibile.',
    ogTitle:  'Soluții Energetice C&I | KÖBER Energy Holding',
    ogDesc:   'PPA solar fără investiție pentru clienți comerciali și industriali. KÖBER finanțează, instalează și operează — plătiți un preț fix pe kWh.',
    locale:   'ro_RO',
  },
};

export default function CIEnergyPage() {
  const [lang, setLang]               = useLang();
  const [contactOpen, setContactOpen] = useState(false);
  const navigate                      = useNavigate();

  const handleSetLang = setLang;
  const text = t[lang];
  const p    = PAGE_TEXT[lang];
  const openContact = () => setContactOpen(true);

  useEffect(() => {
    const get = sel => document.querySelector(sel);
    const sc  = (sel, val) => { const el = get(sel); if (el) el.setAttribute('content', val); };
    const s   = SEO_CI[lang] ?? SEO_CI.EN;
    const prev = { title: document.title, desc: get('meta[name="description"]')?.content, ogT: get('meta[property="og:title"]')?.content, ogD: get('meta[property="og:description"]')?.content, ogU: get('meta[property="og:url"]')?.content, ogL: get('meta[property="og:locale"]')?.content, twT: get('meta[name="twitter:title"]')?.content, twD: get('meta[name="twitter:description"]')?.content };
    document.title = s.title;
    sc('meta[name="description"]',          s.description);
    sc('meta[property="og:title"]',         s.ogTitle);
    sc('meta[property="og:description"]',   s.ogDesc);
    sc('meta[property="og:url"]',           `${window.location.origin}/ci-energy`);
    sc('meta[property="og:locale"]',        s.locale);
    sc('meta[property="og:image"]',         _OG_IMG);
    sc('meta[name="twitter:title"]',        s.ogTitle);
    sc('meta[name="twitter:description"]',  s.ogDesc);
    sc('meta[name="twitter:image"]',        _OG_IMG);
    return () => {
      document.title = prev.title;
      sc('meta[name="description"]',        prev.desc ?? '');
      sc('meta[property="og:title"]',       prev.ogT  ?? '');
      sc('meta[property="og:description"]', prev.ogD  ?? '');
      sc('meta[property="og:url"]',          prev.ogU  ?? '');
      sc('meta[property="og:locale"]',       prev.ogL  ?? '');
      sc('meta[name="twitter:title"]',       prev.twT  ?? '');
      sc('meta[name="twitter:description"]', prev.twD  ?? '');
    };
    // SEO_CI, _OG_IMG are module-level constants; get/sc defined inside effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <div className="bg-[#050505] text-white min-h-screen">
      <Navbar text={text.nav} lang={lang} setLang={handleSetLang} onContact={openContact} />
      <CIHero        p={p.hero}        onContact={openContact} onBack={() => navigate('/')} />
      <CIChallenge   p={p.challenge} />
      <CIModel       p={p.model} />
      <CIHowItWorks  p={p.howItWorks} />
      <CIImpact      p={p.impact} />
      <CIRiskFree    p={p.riskFree} />
      <CIOwnership   p={p.ownership} />
      <CIWhyKober    p={p.whyKober} />
      <CIPortfolio   p={p.portfolio} />
      <CIConsortium  p={p.consortium} />
      <CIFAQ         p={p.faq} />
      <CICTA         p={p.cta} onContact={openContact} />
      <Footer text={text.footer} onContact={openContact} />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} text={text.contact} lang={lang} />
    </div>
  );
}
